import React, { useEffect, useState } from 'react';
import type { WindowWithSquare, SquarePayments, SquarePaymentFormProps } from '../api/types';

const SquarePaymentForm: React.FC<SquarePaymentFormProps> = ({ onToken, appId, locationId, requestTokenRef, className }) => {
  const cardContainerId = 'square-card-container';
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let attached = false;

    function cleanup() {
      const cardDiv = document.getElementById(cardContainerId);
      if (cardDiv) cardDiv.innerHTML = '';
      const btn = document.getElementById('card-button');
      if (btn) btn.replaceWith(btn.cloneNode(true));
      attached = false;
    }

    async function mountCard() {
      const w = window as unknown as WindowWithSquare;
      if (w.Square && !attached) {
        const payments: SquarePayments = w.Square.payments(appId, locationId);
        const card = await payments.card();
        const cardDiv = document.getElementById(cardContainerId);
        if (cardDiv) cardDiv.innerHTML = '';
        await card.attach(cardDiv);
        attached = true;
        // Expose a function to request tokenization
        if (requestTokenRef) {
          requestTokenRef.current = async () => {
            setErrorMsg('');
            const result = await card.tokenize();
            if (result.status === 'OK') {
              return result.token!;
            } else {
              setErrorMsg(result.errors?.[0]?.message || 'Card error');
              return null;
            }
          };
        }
      }
    }

    // Determine environment and script URL
    const env = process.env.REACT_APP_SQUARE_ENV;
    const scriptUrl = env === 'production'
      ? process.env.REACT_APP_SQUARE_PROD_JS
      : process.env.REACT_APP_SQUARE_SANDBOX_JS;

    // Only add the script if not already present
    if (!document.getElementById('square-js')) {
      const script = document.createElement('script');
      script.id = 'square-js';
      script.src = scriptUrl || 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        cleanup();
        mountCard();
      };
      return () => {
        document.body.removeChild(script);
        cleanup();
      };
    } else {
      cleanup();
      mountCard();
      return () => {
        cleanup();
      };
    }
    // Only run on mount/unmount
    // eslint-disable-next-line
  }, []);

  return (
    <div className={className}>
      <div id={cardContainerId}></div>
      {errorMsg && (
        <div className="text-red-600 mt-3 text-center font-sans">{errorMsg}</div>
      )}
    </div>
  );
};

export default SquarePaymentForm;
